import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Globe, MapPin, Star, Play, Camera, TrendingUp, GraduationCap,
  Building2, Package, Printer, Linkedin, MessageCircle, Calendar,
  FileArchive, ExternalLink, Copy, Check, Megaphone, Mail,
  Users, Shield, Eye, Image, Smartphone, BookOpen, Target,
  ChevronDown, ChevronRight, Lock
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

const SITE = process.env.REACT_APP_BACKEND_URL;

function CopyLink({ url, label }) {
  const [ok, setOk] = useState(false);
  const full = url.startsWith("http") ? url : `${SITE}${url}`;
  return (
    <div className="flex items-center gap-2 group">
      <a href={full} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0 truncate text-sm text-blue-400 hover:text-blue-300 transition-colors">
        {label || full}
      </a>
      <button onClick={() => { navigator.clipboard.writeText(full); setOk(true); setTimeout(() => setOk(false), 1500); }}
        className="text-gray-500 hover:text-white transition-colors flex-shrink-0 p-1" title="Copia link">
        {ok ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function Section({ icon: Icon, title, color, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden mb-3">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-4 py-3 bg-white/5 hover:bg-white/8 transition-colors text-left">
        <Icon className={`w-4 h-4 ${color} flex-shrink-0`} />
        <span className="font-bold text-sm text-white flex-1">{title}</span>
        {open ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
      </button>
      {open && <div className="px-4 py-3 space-y-2.5 bg-white/[0.02]">{children}</div>}
    </div>
  );
}

function LinkRow({ icon: Icon, color, label, url, tag, tagColor }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className={`w-4 h-4 ${color} flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-400">{label}</p>
        <CopyLink url={url} />
      </div>
      {tag && <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${tagColor || "bg-white/10 text-gray-400"}`}>{tag}</span>}
    </div>
  );
}

const MANIFESTO_TESTO = `Oggetto: Proposta di Collaborazione — Programma "Comune Digitale": Mappatura Stradale, Virtual Tour 360° e Formazione Digitale per le Micro-Imprese del Territorio

Spett.le Associazione Nazionale Comuni Italiani,

mi rivolgo a Voi con una proposta concreta e immediatamente attuabile per accelerare la digitalizzazione urbana dei Comuni italiani — un tema che oggi non è più un'opzione, ma una necessità strategica per la competitività territoriale.

Mi chiamo Riccardo Rossi, sono Fotografo Certificato Google Street View dal 2016, Google Local Guide di livello avanzato, con all'attivo 9 anni di esperienza sul campo, 7.000 foto pubblicate su Google Maps e 52 milioni di visualizzazioni totali. Opero attraverso schedabusiness.com — Business Profile Solutions, il mio progetto verticale dedicato alla valorizzazione dei Google Business Profile per attività locali.

---

IL PROBLEMA: L'ITALIA DIGITALE A DUE VELOCITÀ

I dati sono inequivocabili:
- Il 46% delle ricerche su Google ha intento locale ("ristorante vicino a me", "farmacia aperta")
- L'88% dei consumatori si fida delle recensioni online quanto dei consigli personali
- Le attività con profilo Google completo ricevono 7x più interazioni
- Un Virtual Tour 360° aumenta l'interesse degli utenti del 67%

Eppure, la realtà dei nostri Comuni racconta un'altra storia:
- Strade mappate in modo errato o incompleto su Google Maps
- Attività locali invisibili: profili inesistenti, sospesi o con dati errati
- Zero Virtual Tour: il patrimonio architettonico, culturale e commerciale italiano è assente dall'esperienza immersiva digitale
- Cittadini over-55 (il 42% della popolazione) esclusi dagli strumenti digitali per mancanza di formazione accessibile

Nel 2026, non essere su Google Maps equivale a non essere nell'elenco telefonico negli anni '80. Con una differenza: oggi il "telefono" è in tasca a 50 milioni di italiani, e nessuno apre più le Pagine Gialle.

---

LA PROPOSTA: PROGRAMMA "COMUNE DIGITALE"

Un pacchetto chiavi-in-mano per ogni Comune aderente, composto da tre pilastri:

PILASTRO 1 — Mappatura Stradale e Verifica Google Maps
- Audit completo della mappatura stradale del territorio comunale su Google Maps
- Segnalazione e correzione di errori cartografici (strade mancanti, nomi errati, sensi unici sbagliati)
- Verifica e aggiornamento dei punti di interesse (POI) comunali: municipio, scuole, centri sportivi, parchi, monumenti
- Report finale consegnato all'ufficio tecnico del Comune

PILASTRO 2 — Virtual Tour 360° Immersivo
- 1 Virtual Tour 360° professionale INCLUSO del luogo più rappresentativo del Comune (piazza principale, centro storico, monumento simbolo, sede municipale)
- Realizzato con GoPro MAX da Fotografo Certificato Google Street View
- Pubblicazione diretta su Google Maps e Google Street View
- Il Virtual Tour diventa patrimonio digitale permanente del Comune, accessibile da chiunque nel mondo

PILASTRO 3 — Formazione Digitale per le Micro-Imprese
Una sessione formativa gratuita per gli iscritti (inclusa nel pacchetto Comune) in collaborazione con:
- Pro Loco (settore Ho.Re.Ca.: hotel, ristoranti, agriturismi)
- Confcommercio e Confesercenti (commercio e servizi)
- Confindustria e Confartigianato (artigianato e manifattura locale)

Contenuto della formazione:
- Come valorizzare il proprio Google Business Profile
- Come rispondere professionalmente alle recensioni
- Come ottimizzare foto e informazioni per apparire nelle ricerche locali
- Come interpretare le statistiche di Google Business Profile

Formato: Sessione live (in presenza o via Google Meet), 2-3 ore, con materiale didattico. Registrazione disponibile per gli assenti.

Il messaggio chiave per le associazioni di categoria:
"Ricordate ai vostri iscritti che l'investimento in formazione professionale è deducibile al 100% ai fini fiscali, con pagamento rateizzabile in 3 rate a TAEG 0%. Essere formati da un Google Street View Trusted a costo zero non capita tutti i giorni."

---

INVESTIMENTO E PRICING

Pacchetto "Comune Digitale" — Prezzo di Lancio

Audit mappatura stradale + correzioni: Valore €400 → INCLUSO
Virtual Tour 360° luogo simbolo (GoPro MAX): Valore €499 → IN REGALO
Sessione formativa micro-imprese (2-3h): Valore €449 → INCLUSA
Report + materiale didattico: Valore €150 → INCLUSO
TOTALE: Valore reale €1.498 → Prezzo di lancio €999

Risparmio: €499 — Il Virtual Tour 360° è il nostro regalo al Comune.

Modalità di pagamento:
- Pagamento unico: €999
- Rate: €333 x 3 (TAEG 0%, senza interessi)
- Investimento 100% deducibile per l'Ente

Sconti Volume per Reti di Comuni:
- 1-5 Comuni: €999
- 6-15 Comuni: €899 (-10%)
- 16-30 Comuni: €799 (-20%)
- 30+ Comuni: €699 (-30%)

Per le Unioni di Comuni e le Comunità Montane, il prezzo viene calcolato sulla base del pacchetto complessivo con ulteriori agevolazioni.

---

COSA CI GUADAGNA IL COMUNE?

Per il Sindaco e la Giunta:
Una settimana di campagna marketing dedicata con:
- Comunicato stampa professionale: "Il Comune di [Nome] investe nella formazione digitale delle micro-imprese del territorio"
- Grafiche social personalizzate per i canali ufficiali del Comune
- Copertura fotografica dell'evento formativo
- Riconoscimento pubblico come Comune promotore di innovazione digitale e sostegno alle imprese locali

Questo si traduce in:
- Visibilità politica concreta (formazione, innovazione, sostegno alle imprese)
- Aumento del turismo digitale tramite Virtual Tour 360° accessibile globalmente
- Soddisfazione dei commercianti che ricevono formazione qualificata a costo zero
- Allineamento con l'Agenda Digitale nazionale e i fondi PNRR per la digitalizzazione

Per i Cittadini:
- Strade mappate correttamente = meno turisti persi, meno segnalazioni
- Virtual Tour = promozione permanente del patrimonio locale
- Imprese formate = servizi migliori, più recensioni positive, più attrattività

---

PERCHÉ schedabusiness

- 9 anni di esperienza nel settore Google Maps e Business Profile
- 7.000 foto pubblicate su Google Maps — competenza tecnica verificata
- 52 milioni di visualizzazioni — risultati misurabili e documentati
- Fotografo Certificato Google Street View dal 2016 — riconoscimento ufficiale
- Approccio "Problema → Soluzione": ogni intervento nasce da un'esigenza concreta
- Verticalità territoriale: conoscenza diretta del tessuto imprenditoriale toscano e italiano
- Strumenti accessibili: interfaccia semplice, spiegazioni chiare, adatto anche a utenti over-55

---

PROSSIMI PASSI

1. Videochiamata conoscitiva (30 minuti) per presentare il programma al referente ANCI
2. Progetto pilota su 3-5 Comuni selezionati (Toscana, Lombardia, Veneto o a scelta)
3. Valutazione risultati dopo 90 giorni
4. Estensione nazionale con convenzione ANCI

---

RISORSE ONLINE

Sito web schedabusiness: ${SITE}
Convenzione Enti: ${SITE}/enti
Virtual Tour 360°: ${SITE}/deducibile100
Consulenza: ${SITE}/consulenza
Presentazione interattiva: ${SITE}/scopri

---

Resto a disposizione per un incontro conoscitivo — anche in videochiamata via Google Meet — per approfondire i contenuti del programma e personalizzare il progetto.

Cordiali saluti,

Riccardo Rossi
Fotografo Certificato Google Street View dal 2016
Google Local Guide · Siena

schedabusiness.com — Business Profile Solutions
Tel/WhatsApp: +39 327 383 9782
Calendly: https://calendly.com/riccardorossi1984
Web: ${SITE}

Made in Toscana, Italy

"Nel 2026, non essere su Google Maps è come non essere nell'elenco telefonico negli anni '80. La differenza? Oggi il telefono è in tasca a 50 milioni di italiani."`;

function ManifestoANCI() {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(MANIFESTO_TESTO);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };
  return (
    <div className="border border-blue-500/20 rounded-lg overflow-hidden">
      <div className="flex items-center gap-3 px-3 py-2.5">
        <Mail className="w-4 h-4 text-blue-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-400">Manifesto ANCI — Digitalizzazione Urbana</p>
          <p className="text-sm text-blue-300">Programma "Comune Digitale" €999</p>
        </div>
        <Badge className="bg-[#ff6b35]/20 text-[#ff6b35] border-[#ff6b35]/30 text-[9px] flex-shrink-0">NEW</Badge>
      </div>
      <div className="flex gap-2 px-3 pb-2.5">
        <button onClick={handleCopy} data-testid="copy-manifesto-btn"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${copied ? "bg-green-500/20 text-green-400 border border-green-500/30" : "bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30"}`}>
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copiata!" : "Copia email completa"}
        </button>
        <button onClick={() => setExpanded(!expanded)} data-testid="preview-manifesto-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10 transition-all">
          <Eye className="w-3 h-3" />
          {expanded ? "Nascondi" : "Anteprima"}
        </button>
      </div>
      {expanded && (
        <div className="px-3 pb-3">
          <pre className="text-[11px] text-gray-400 whitespace-pre-wrap bg-white/[0.03] rounded-lg p-3 max-h-[400px] overflow-y-auto border border-white/5 leading-relaxed font-sans">
            {MANIFESTO_TESTO}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function MappaInternaPage() {
  return (
    <div className="min-h-screen bg-[#0c1220] text-white" data-testid="mappa-interna-page">

      {/* HEADER */}
      <header className="border-b border-white/10 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Lock className="w-5 h-5 text-[#ff6b35]" />
            <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px]">USO INTERNO</Badge>
          </div>
          <h1 className="text-2xl font-black font-google" data-testid="mappa-interna-title">
            <span className="text-[#EA4335]">S</span><span className="text-[#FBBC04]">B</span><span className="text-[#34A853]">P</span>
            <span className="text-white"> — Mappa Completa</span>
          </h1>
          <p className="text-gray-500 text-xs mt-1">Tutti i link, risorse e asset del progetto schedabusiness. Aggiornato Marzo 2026.</p>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">

        {/* PAGINE PUBBLICHE */}
        <Section icon={Globe} title="Pagine Pubbliche" color="text-[#4285F4]" defaultOpen={true}>
          <LinkRow icon={Globe} color="text-[#ff6b35]" label="Homepage" url="/" tag="MAIN" tagColor="bg-[#ff6b35]/20 text-[#ff6b35]" />
          <LinkRow icon={Eye} color="text-purple-400" label="Scopri (Funnel Interattivo)" url="/scopri" tag="QR CODE" tagColor="bg-purple-500/20 text-purple-400" />
          <LinkRow icon={BookOpen} color="text-white" label="schedabusiness Hub (Linktree)" url="/links" />
          <LinkRow icon={GraduationCap} color="text-[#ff6b35]" label="Deducibile 100%" url="/deducibile100" tag="NEW" tagColor="bg-green-500/20 text-green-400" />
          <LinkRow icon={Building2} color="text-green-400" label="Convenzione Enti" url="/enti" />
          <LinkRow icon={Camera} color="text-teal-400" label="Virtual Tour 360°" url="/deducibile100" />
          <LinkRow icon={Printer} color="text-amber-400" label="Brand Kit Tipografia" url="/brand-kit" />
          <LinkRow icon={Users} color="text-[#ff6b35]" label="Per Agenzie" url="/per-agenzie" />
          <LinkRow icon={Package} color="text-amber-400" label="Merchandising" url="/merchandising" />
        </Section>

        {/* PACCHETTI */}
        <Section icon={Star} title="Pacchetti Servizio" color="text-[#FBBC04]">
          <LinkRow icon={Play} color="text-blue-400" label="Start — €99" url="/start" tag="€33x3" tagColor="bg-blue-500/20 text-blue-400" />
          <LinkRow icon={Star} color="text-red-400" label="Plus — €299" url="/plus" tag="BEST SELLER" tagColor="bg-red-500/20 text-red-400" />
          <LinkRow icon={TrendingUp} color="text-amber-400" label="Rooftop — €599" url="/rooftop" tag="€49,91/m" tagColor="bg-amber-500/20 text-amber-400" />
          <LinkRow icon={Camera} color="text-teal-400" label="Virtual Tour 360° — €499" url="/deducibile100" tag="START INCL." tagColor="bg-teal-500/20 text-teal-400" />
        </Section>

        {/* LANDING ADS (Giorgio) */}
        <Section icon={Megaphone} title="Landing Google Ads (Giorgio)" color="text-[#EA4335]">
          <LinkRow icon={Play} color="text-blue-400" label="Ads → Start" url="/ads/start" />
          <LinkRow icon={Star} color="text-red-400" label="Ads → Plus" url="/ads/plus" />
          <LinkRow icon={TrendingUp} color="text-amber-400" label="Ads → Rooftop" url="/ads/rooftop" />
          <LinkRow icon={Camera} color="text-teal-400" label="Ads → 360°" url="/ads/360" />
          <LinkRow icon={GraduationCap} color="text-[#ff6b35]" label="Ads → Consulenza" url="/ads/academy" />
        </Section>

        {/* CONTATTI */}
        <Section icon={MessageCircle} title="Contatti & CTA" color="text-[#25D366]">
          <LinkRow icon={MessageCircle} color="text-green-400" label="WhatsApp" url="https://wa.me/+393273839782" />
          <LinkRow icon={Calendar} color="text-blue-400" label="Calendly" url="https://calendly.com/riccardorossi1984" />
          <LinkRow icon={Linkedin} color="text-[#0077B5]" label="LinkedIn Premium" url="https://www.linkedin.com/in/riccardorossi1984googletnx/" />
          <LinkRow icon={Globe} color="text-red-400" label="PayPal" url="https://www.paypal.me/Riccarossi" />
        </Section>

        {/* ASSET & DOWNLOAD */}
        <Section icon={FileArchive} title="Asset & Download" color="text-[#34A853]">
          <LinkRow icon={FileArchive} color="text-[#ff6b35]" label="Scarica ZIP Brand Kit" url="/api/brand-kit/download" tag="10.7MB" tagColor="bg-[#ff6b35]/20 text-[#ff6b35]" />
          <LinkRow icon={Image} color="text-blue-400" label="OG Image WhatsApp (JPEG)" url="/og-whatsapp.jpg" tag="68KB" tagColor="bg-blue-500/20 text-blue-400" />
          <LinkRow icon={Smartphone} color="text-green-400" label="Favicon schedabusiness" url="/favicon-sbp.png" />
        </Section>

        {/* VIDEO YouTube */}
        <Section icon={Play} title="Video YouTube" color="text-red-500">
          <LinkRow icon={Play} color="text-red-400" label="Spot schedabusiness" url="https://www.youtube-nocookie.com/embed/SgHZdoQ_nEc" />
          <LinkRow icon={Play} color="text-red-400" label="Video Recensione" url="https://www.youtube-nocookie.com/embed/fUVpkf_Rb38" />
          <LinkRow icon={Play} color="text-red-400" label="Virtual Tour Demo" url="https://www.youtube-nocookie.com/embed/q-V8oipCpxA" />
        </Section>

        {/* DOCUMENTI INTERNI */}
        <Section icon={Mail} title="Documenti & Email" color="text-amber-400">
          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-400">Email Enti Ho.Re.Ca.</p>
              <p className="text-sm text-amber-300">/app/brand-kit/EMAIL-ENTI-HORECA.md</p>
            </div>
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[9px]">PRONTA</Badge>
          </div>
          <ManifestoANCI />
        </Section>

        {/* STATS RICCARDO */}
        <Section icon={Shield} title="Credenziali Riccardo" color="text-[#ff6b35]">
          <div className="grid grid-cols-3 gap-3">
            {[
              { n: "9", l: "Anni", c: "text-[#ff6b35]" },
              { n: "7K", l: "Foto Maps", c: "text-[#4285F4]" },
              { n: "52M", l: "Views", c: "text-[#34A853]" },
            ].map(s => (
              <div key={s.l} className="bg-white/5 rounded-xl p-3 text-center border border-white/10">
                <p className={`text-2xl font-black ${s.c}`}>{s.n}</p>
                <p className="text-[10px] text-gray-500 uppercase">{s.l}</p>
              </div>
            ))}
          </div>
          <div className="mt-2 space-y-1.5 text-xs text-gray-400">
            <p>Fotografo Certificato Google Street View dal 2016</p>
            <p>Google Local Guide — Livello avanzato</p>
            <p>Riccardo Rossi · Siena · +39 327 383 9782</p>
          </div>
        </Section>

        {/* TEAM */}
        <Section icon={Users} title="Team" color="text-purple-400">
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#ff6b35]/20 rounded-full flex items-center justify-center"><span className="text-xs font-bold text-[#ff6b35]">RR</span></div>
              <div><p className="font-bold text-white">Riccardo Rossi</p><p className="text-[10px] text-gray-500">Creative Director · Google Street View Trusted</p></div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center"><span className="text-xs font-bold text-blue-400">GA</span></div>
              <div><p className="font-bold text-white">Giorgio Antenangeli</p><p className="text-[10px] text-gray-500">Google Ads</p></div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center"><span className="text-xs font-bold text-purple-400">JV</span></div>
              <div><p className="font-bold text-white">Jessica Ventura</p><p className="text-[10px] text-gray-500">LinkedIn Positioning</p></div>
            </div>
          </div>
        </Section>

        {/* PREZZI RIEPILOGO */}
        <Section icon={Target} title="Riepilogo Prezzi" color="text-green-400">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-gray-500 border-b border-white/10">
                <th className="text-left py-2">Pacchetto</th><th className="text-right py-2">Prezzo</th><th className="text-right py-2">Rate x3</th><th className="text-right py-2">Mensile</th>
              </tr></thead>
              <tbody className="text-gray-300">
                {[
                  ["Start", "€99", "€33", "€8,25"],
                  ["Plus", "€299", "€99,66", "€24,91"],
                  ["Rooftop", "€599", "€199,66", "€49,91"],
                  ["Virtual Tour 360°", "€499", "—", "—"],
                  ["Enti (Opz. C 50/50)", "da €139", "—", "—"],
                  ["Comuni (Lancio)", "€999", "€333", "—"],
                ].map(r => (
                  <tr key={r[0]} className="border-b border-white/5">
                    <td className="py-2 font-bold">{r[0]}</td><td className="py-2 text-right">{r[1]}</td><td className="py-2 text-right">{r[2]}</td><td className="py-2 text-right">{r[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* FOOTER */}
        <footer className="text-center py-8 border-t border-white/10 mt-6">
          <p className="text-gray-600 text-[10px]">Documento interno — Non condividere</p>
          <p className="text-gray-700 text-[9px] mt-1">schedabusiness.com — Business Profile Solutions · Marzo 2026</p>
        </footer>
      </div>
    </div>
  );
}
